import React, { forwardRef, useCallback } from 'react';
import { Alert } from '@mui/material';
import { useSnackbar } from 'notistack';

/**
 * Custom notistack snackbar that renders as a MUI Alert.
 * Registered via SnackbarProvider's Components prop.
 *
 * notistack passes a snackbar's `action` (a ReactNode, or a
 * `(snackbarId) => ReactNode` callback) to the custom component — the default
 * SnackbarContent renders it, but a custom component must do it itself. We
 * resolve it and hand it to MUI Alert's `action` slot. Without this, every
 * snackbar with an action button (e.g. the "Autorizar" CTA from
 * MissingScopeHandler) rendered as text-only with no buttons.
 */
export const AlertSnackbar = forwardRef(({ id, message, variant, action, ...rest }, ref) => {
  const { closeSnackbar } = useSnackbar();
  const handleClose = useCallback(() => closeSnackbar(id), [id, closeSnackbar]);

  const resolvedAction = typeof action === 'function' ? action(id) : action;

  return (
    <Alert
      ref={ref}
      severity={variant || 'info'}
      variant="filled"
      // When the snackbar carries its own action(s) (which include their own
      // close/dismiss control), show those instead of the default close "X".
      action={resolvedAction ?? undefined}
      onClose={resolvedAction ? undefined : handleClose}
      sx={{
        fontSize: '0.95rem',
        minWidth: 300,
        maxWidth: 500,
        alignItems: 'center',
      }}
      {...rest}
    >
      {message}
    </Alert>
  );
});

AlertSnackbar.displayName = 'AlertSnackbar';
